/* eslint-disable require-await */
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Icon,
  Button,
  Loader,
  Pagination,
  Link as RouterLink
} from '@deque/cauldron-react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type ProductSlugs } from '@deque/billing-utils';
import type {
  UniversalIssue,
  UniversalIssueWithScreenshot
} from '@deque/axe-devtools-issues-utils';

import usePagination, {
  type Pagination as PaginationType
} from '../../hooks/usePagination';
import Link from '../Link';
import IssueDetails from './IssueDetails';
import CopyToClipboard from '../CopyToClipboard';
import { useAuthContext } from '../../contexts/auth';
import useSearchParams from '../../hooks/useSearchParams';
import { useIntegrations } from '../../../app/hooks/useIntegrations';
import useIntegrationConnections from '../../../app/hooks/useIntegrationConnections';
import { getIntegrationProductInfoFromSlug } from '../../utils/get-integration-product-name-from-slug';
import styles from './Issues.css';
import { filterConnectedIntegrationSlugs } from '../../utils/integrations-client/filterConnectedIntegrationSlugs';
import {
  ProductNames,
  SupportedShareableIssueIntegrationProductSlugs
} from '../../constants';
import { useFeatureFlagState } from '../../contexts/featureFlags';
import {
  SendToIntegrationContainer,
  useSendToIntegrationLogic
} from '../../ejectable';
import { type SendIssueEventArgs } from '../../ejectable/hooks/useSendToIntegrationLogic';
import {
  reportSendIssueFailed,
  reportSendIssueInitiate,
  reportSendIssueSuccess
} from '../../utils/integrations-client/jira/reportSendIssueAnalyticEvent';
import { useGlobalToast } from '../../contexts/globalToast';

export type GetIssueURL = (
  issue: UniversalIssueWithScreenshot,
  pagination: PaginationType<UniversalIssueWithScreenshot>
) => string;

export interface Props {
  issues: UniversalIssueWithScreenshot[];
  testId: string;
  issuesProductSourceSlug: ProductSlugs;
  onIssueView?: (currentIssue: UniversalIssueWithScreenshot) => void;
  onShare?: () => Promise<void>;
  initialPage?: number;
  getIssueURL?: GetIssueURL;
  noScreenshotExplanation?: React.ReactNode;
  onIntegrationShare?: () => void;
  issueIDExtractorFn: (issue: UniversalIssue) => string;
}

const getIssueDefaultURL = (issue: UniversalIssueWithScreenshot): string =>
  `${window.location.origin}/issues/${issue.issueId}`;

const Issues: React.FC<Props> = ({
  issues,
  onIssueView,
  onShare,
  initialPage,
  getIssueURL = getIssueDefaultURL,
  noScreenshotExplanation,
  onIntegrationShare,
  issueIDExtractorFn
}) => {
  const { t } = useTranslation();

  const history = useHistory();
  const searchParams = useSearchParams();
  const { setContents } = useGlobalToast();

  const { user } = useAuthContext();
  const hasIntegrationsV1 = useFeatureFlagState('integrations_v1');

  const {
    enabledIntegrationProductSlugs,
    canAccessIntegrationConfiguration,
    activeEnterprise,
    isEnterpriseAdmin
  } = useIntegrations();

  const {
    data: connections,
    loading: connectionsLoading,
    error: connectionsError
  } = useIntegrationConnections(
    activeEnterprise?.id,
    user?.token,
    !canAccessIntegrationConfiguration
  );

  const pagination = usePagination({
    items: issues,
    itemsPerPage: 1,
    initialPage
  });

  const currentIssue = pagination.currentPageItems[0];

  const issuesFn = useCallback(async () => [currentIssue], [currentIssue]);

  const onSuccessSendIssue = useCallback(
    (args: SendIssueEventArgs) => {
      const { displayName } = getIntegrationProductInfoFromSlug(
        args.integrationProductSlug as ProductSlugs
      );
      const successMessage = t(`{{integrationName}} issue sent`, {
        integrationName: displayName
      });

      setContents(successMessage, 'confirmation');
      reportSendIssueSuccess(args);
      onIntegrationShare?.();
    },
    [onIntegrationShare]
  );

  const sendToIntegrationLogicConfig = useMemo(
    () => ({
      hostProduct: { name: ProductNames.axe },
      apiHostname: window.appConfig.API_SERVER_URL || '',
      userEnterpriseId: activeEnterprise?.id || '',
      authFn: async () => user?.token || '',
      userId: user?.id,
      issueIDExtractorFn,
      issuesFn,
      onSuccessSendIssue,
      onErrorSendIssue: reportSendIssueFailed,
      onInitiateSendIssue: reportSendIssueInitiate
    }),
    [currentIssue, user, issuesFn, onIntegrationShare, onSuccessSendIssue]
  );

  const {
    loading: sendToIntegrationLoading,
    error: sendToIntegrationError,
    props: sendToIntegrationProps
  } = useSendToIntegrationLogic(sendToIntegrationLogicConfig);

  const connectedIntegrations = filterConnectedIntegrationSlugs(
    enabledIntegrationProductSlugs,
    connections
  ).filter(integration =>
    SupportedShareableIssueIntegrationProductSlugs.includes(integration)
  );

  const shouldShare = !!onShare;

  useEffect(() => {
    onIssueView?.(currentIssue);
  }, [currentIssue]);

  useEffect(() => {
    searchParams.set('issueIndex', pagination.currentPage.toString());
    searchParams.set('ruleId', currentIssue.ruleId);
    // use replace instead of push so that we don't add an extra entry to history stack
    history.replace({ search: searchParams.toString() });
  }, [pagination.currentPage, currentIssue.ruleId]);

  if (connectionsLoading || sendToIntegrationLoading) {
    return <Loader label={t('Loading...')} />;
  }

  if (connectionsError || sendToIntegrationError) {
    return <div>{t('Error loading connections')}</div>;
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.actions}>
        <div className={styles.actionGroup}>
          <RouterLink
            thin
            variant="button-secondary"
            href={currentIssue.helpUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon type="external-link" label={t('opens in a new window')} />
            {t('MORE INFO')}
          </RouterLink>
          <CopyToClipboard
            onCopy={onShare}
            text={getIssueURL(currentIssue, pagination)}
            confirmationMessage={t(
              'The issue link has been copied to your clipboard.'
            )}
          >
            <Button thin variant="secondary" className={styles.shareButton}>
              <Icon type={shouldShare ? 'share-nodes' : 'link'} />
              {shouldShare ? t('SHARE ISSUE') : t('COPY ISSUE LINK')}
            </Button>
          </CopyToClipboard>
          {hasIntegrationsV1 &&
            !connectedIntegrations.length &&
            isEnterpriseAdmin &&
            enabledIntegrationProductSlugs
              .filter(integration =>
                SupportedShareableIssueIntegrationProductSlugs.includes(
                  integration
                )
              )
              .map(integration => (
                <Link
                  key={integration}
                  thin
                  variant="button-secondary"
                  url="/configuration/integrations"
                  className={styles.shareButton}
                >
                  <Icon type="share-nodes" />
                  {t(`CONNECT TO {{ integration }}`, {
                    integration: getIntegrationProductInfoFromSlug(
                      integration as unknown as ProductSlugs
                    ).displayName.toLocaleUpperCase()
                  })}
                </Link>
              ))}
          <SendToIntegrationContainer {...sendToIntegrationProps} />
        </div>
        <Pagination
          thin
          totalItems={pagination.totalItems}
          itemsPerPage={pagination.itemsPerPage}
          currentPage={pagination.currentPage}
          firstPageLabel={t('First Issue')}
          nextPageLabel={t('Next Issue')}
          previousPageLabel={t('Previous Issue')}
          lastPageLabel={t('Last Issue')}
          statusLabel={
            <span data-testid="issues-pagination">
              <strong>{pagination.currentPage}</strong> {t('of')}{' '}
              <strong>{pagination.totalItems}</strong>
            </span>
          }
          onNextPageClick={pagination.handleNextPage}
          onFirstPageClick={pagination.handleFirstPage}
          onPreviousPageClick={pagination.handlePreviousPage}
          onLastPageClick={pagination.handleLastPage}
        />
      </div>
      <IssueDetails
        issue={pagination.currentPageItems[0]}
        issueOrder={pagination.currentPage}
        noScreenshotExplanation={noScreenshotExplanation}
      />
    </div>
  );
};

export default Issues;
